<template>
	<div>
		<Breadcrumbs main="Support Management" title="Support Details" />

		<div class="container-fluid">
			<div class="row">
				<div class="col-sm-12">
					<div class="card">
						<div class="card-body">
							<div class="row mb-3">
								<div class="col-10">
									<button
										class="btn btn-info"
										v-if="support.status != 3 || support.work_status != 2"
										@click="openStatusModal"
									>
										Update Status
									</button>
									<router-link
										v-if="work_service && work_service.id"
										:to="{ name: 'complaint_details', params: { id: work_service.id } }"
										class="btn btn-secondary ms-2"
									>
										Open Work Service
									</router-link>
								</div>
								<div class="col-2">
									<a href="#" @click.prevent="$router.go(-1)" class="btn btn-primary float-end">
										<i class="bx bx-chevrons-left"></i> Back
									</a>
								</div>
							</div>

							<div class="row">
								<div class="col-md-6">
									<div class="border border-light">
										<h4 class="font-size-16 bg-primary border border-top-0 border-start-0 border-end-0 px-3 py-2 mb-3">
											Support Details
										</h4>
										<div class="table table-responsive">
											<table class="details-table table-nowrap mb-0">
												<tbody>
													<tr>
														<th scope="row">IMEI</th>
														<th>:</th>
														<td>{{ support.imei || support.device_imei || '-' }}</td>
													</tr>
													<tr>
														<th scope="row">ICCID</th>
														<th>:</th>
														<td>{{ support.iccid || '-' }}</td>
													</tr>
													<tr>
														<th scope="row">Support Type</th>
														<th>:</th>
														<td>{{ supportTypeLabel(support.support_type) }}</td>
													</tr>
													<tr>
														<th scope="row">Requested By</th>
														<th>:</th>
														<td>
															<span v-if="support.requested_by == 1">Customer ({{ support.customer_name_user || support.customer_name || '-' }})</span>
															<span v-else-if="support.requested_by == 2">Dealer ({{ support.dealer_name || '-' }})</span>
															<span v-else>-</span>
														</td>
													</tr>
													<tr>
														<th scope="row">Mobile</th>
														<th>:</th>
														<td>{{ support.mobile || '-' }}</td>
													</tr>
													<tr>
														<th scope="row">Details</th>
														<th>:</th>
														<td>{{ support.details || '-' }}</td>
													</tr>
													<tr>
														<th scope="row">Status</th>
														<th>:</th>
														<td>
															<span class="badge rounded-pill bg-warning" v-if="support.status == 1">Pending</span>
															<span class="badge rounded-pill bg-info" v-else-if="support.status == 2">Ongoing</span>
															<span class="badge rounded-pill bg-success" v-else-if="support.status == 3">Completed</span>
															<span class="badge rounded-pill bg-primary" v-else-if="support.status == 4">To Speedtech</span>
															<span class="badge rounded-pill bg-secondary" v-else-if="support.status == 5">To Technician</span>
															<span class="badge rounded-pill bg-dark" v-else-if="support.status == 6">To Service</span>
															<span v-else>{{ support.status }}</span>
														</td>
													</tr>
													<tr>
														<th scope="row">Technician</th>
														<th>:</th>
														<td>{{ support.technician_name || '-' }}</td>
													</tr>
													<tr>
														<th scope="row">Created Date</th>
														<th>:</th>
														<td>{{ support.created_date || '-' }}</td>
													</tr>
													<tr>
														<th scope="row">Added By</th>
														<th>:</th>
														<td>{{ support.added_user_name || '-' }}</td>
													</tr>
													<tr>
														<th scope="row">Remarks</th>
														<th>:</th>
														<td>{{ support.remarks || '-' }}</td>
													</tr>
												</tbody>
											</table>
										</div>
									</div>
								</div>

								<div class="col-md-6">
									<div class="border border-light">
										<h4 class="font-size-16 bg-primary border border-top-0 border-start-0 border-end-0 px-3 py-2 mb-3">
											Payment / Linked Service
										</h4>
										<div class="table table-responsive">
											<table class="details-table table-nowrap mb-0">
												<tbody>
													<tr>
														<th scope="row">Total Amount</th>
														<th>:</th>
														<td>{{ support.total_amount ?? 0 }}</td>
													</tr>
													<tr>
														<th scope="row">Payment Status</th>
														<th>:</th>
														<td>{{ support.payment_status ?? '-' }}</td>
													</tr>
													<tr>
														<th scope="row">Cash / Online</th>
														<th>:</th>
														<td>{{ support.cash_in_hand ?? 0 }} / {{ support.cash_online ?? 0 }}</td>
													</tr>
													<tr>
														<th scope="row">Payment ID</th>
														<th>:</th>
														<td>{{ support.payment_id || '-' }}</td>
													</tr>
													<tr>
														<th scope="row">Work Service</th>
														<th>:</th>
														<td>
															<span v-if="work_service && work_service.id">#{{ work_service.id }} ({{ workServiceStatusLabel(work_service.status) }})</span>
															<span v-else>-</span>
														</td>
													</tr>
													<tr>
														<th scope="row">Completed Date</th>
														<th>:</th>
														<td>{{ support.completed_date || '-' }}</td>
													</tr>
												</tbody>
											</table>
										</div>
									</div>
								</div>
							</div>

							<div class="row mt-3">
								<div class="col-12">
									<div class="border border-light">
										<h4 class="font-size-16 bg-primary border border-top-0 border-start-0 border-end-0 px-3 py-2 mb-3">
											Support Logs
										</h4>
										<div class="table-responsive product-table">
											<table class="display">
												<thead>
													<tr align="center">
														<th>Sl.No</th>
														<th>Date</th>
														<th>Staff</th>
														<th>Status</th>
														<th>Remarks</th>
													</tr>
												</thead>
												<tbody v-if="Boolean(logs.length)">
													<tr v-for="(log, index) in logs" :key="log.id" align="center">
														<td>{{ index + 1 }}</td>
														<td>{{ log.date || '-' }}</td>
														<td>{{ log.added_user_name || '-' }}</td>
														<td>{{ statusLabel(log.status) }}</td>
														<td>{{ log.remarks || '-' }}</td>
													</tr>
												</tbody>
												<tbody v-else>
													<tr>
														<td colspan="5" align="center">No logs available</td>
													</tr>
												</tbody>
											</table>
										</div>
									</div>
								</div>
							</div>
						</div>
					</div>
				</div>
			</div>
		</div>

		<DynamicModal :title="modal_title">
			<template #body>
				<form @submit.prevent="submitStatusUpdate">
					<div class="row">
						<div class="col-md-12 form-group">
							<label class="control-label">Select Status<span class="text-danger">*</span></label>
							<select class="form-select" v-model="status_type" @change="onStatusTypeChange">
								<option value="">--Select--</option>
								<option value="3">Completed</option>
								<option value="4">To Speedtech</option>
								<option value="5">Assign to Technician</option>
								<option value="6" :disabled="support.status == 6">To Service</option>
							</select>
						</div>

						<div class="col-md-12 form-group mt-2" v-if="status_type == '5'">
							<label class="control-label">Technician<span class="text-danger">*</span></label>
							<select class="form-select" v-model="technician_id">
								<option value="">--Select Technician--</option>
								<option v-for="tech in technicians" :key="tech.id" :value="String(tech.id)">
									{{ tech.name }}
								</option>
							</select>
						</div>

						<div class="col-md-12 form-group mt-2" v-if="status_type == '6'">
							<label class="control-label">Service Type (optional)</label>
							<select class="form-select" v-model="service_type">
								<option value="">Auto from support type</option>
								<option value="4">No Data</option>
								<option value="5">Tracking Issue</option>
								<option value="6">Hardware Issue</option>
								<option value="7">Software Issue</option>
								<option value="8">Alert Fail</option>
								<option value="9">Other</option>
							</select>
						</div>

						<div class="col-md-12 form-group mt-2" v-if="status_type == '6'">
							<label class="control-label">Vehicle Number</label>
							<input class="form-control" type="text" v-model="vehicle_number" placeholder="Optional" />
						</div>

						<div class="col-md-12 form-group mt-2">
							<label class="control-label">Remarks<span class="text-danger">*</span></label>
							<textarea class="form-control" rows="3" v-model="remarks" required></textarea>
						</div>
					</div>

					<div class="row mt-3">
						<div class="col-12 text-end">
							<button type="button" class="btn btn-secondary me-2" @click="closeStatusModal">Cancel</button>
							<button type="submit" class="btn btn-success" :disabled="submitting">
								<span v-if="submitting" class="spinner-border spinner-border-sm me-2"></span>
								{{ submitting ? 'Saving...' : 'Update' }}
							</button>
						</div>
					</div>
				</form>
			</template>
		</DynamicModal>
	</div>
</template>

<script>
import DynamicModal from '@/components/dynamic_modal.vue';
import { Modal } from 'bootstrap';
import { onMounted } from 'vue';

export default {
	name: 'SupportDetails',
	components: {
		DynamicModal,
	},
	setup() {
		let dynamicmodal = null;

		onMounted(() => {
			const el = document.getElementById('dynamicmodal');
			if (el) {
				dynamicmodal = new Modal(el);
			}
		});

		const showModal = () => {
			if (!dynamicmodal) {
				const el = document.getElementById('dynamicmodal');
				if (el) dynamicmodal = new Modal(el);
			}
			dynamicmodal?.show();
		};

		const hideModal = () => {
			dynamicmodal?.hide();
		};

		return {
			showModal,
			hideModal,
		};
	},
	data() {
		return {
			token: '',
			support: {},
			work_service: null,
			logs: [],
			modal_title: 'Update Support Status',
			status_type: '',
			technician_id: '',
			service_type: '',
			vehicle_number: '',
			remarks: '',
			technicians: [],
			submitting: false,
		};
	},
	mounted() {
		this.fetchDetail();
	},
	methods: {
		getToken() {
			if (localStorage.getItem('airotrack_staff')) {
				const airotrack_staff = JSON.parse(localStorage.getItem('airotrack_staff'));
				if (airotrack_staff.token) {
					this.token = airotrack_staff.token;
				} else {
					this.$swal('', 'Failed to fetch token, please login again', 'error');
					this.$router.push({ name: 'login' });
				}
			} else {
				this.$swal('', 'Session expired, please login again', 'error');
				this.$router.push({ name: 'login' });
			}
		},
		supportTypeLabel(type) {
			const map = {
				1: 'No Data',
				2: 'Tracking Issue',
				3: 'Software Issue',
				4: 'Alert Fail',
				5: 'CDCA Alert',
				6: '3rd Party App Point',
				7: 'Other',
			};
			return map[type] || 'N/A';
		},
		statusLabel(status) {
			const map = {
				1: 'Pending',
				2: 'Ongoing',
				3: 'Completed',
				4: 'To Speedtech',
				5: 'To Technician',
				6: 'To Service',
			};
			return map[status] || status;
		},
		workServiceStatusLabel(status) {
			const map = {
				1: 'Pending',
				2: 'Ongoing',
				3: 'Completed',
				4: 'To Vendor',
				5: 'Component Replacement',
			};
			return map[status] || status;
		},
		fetchDetail() {
			this.getToken();
			const supportId = this.$route.params.id;
			this.$axios
				.get('/staffs/supportDetail?support_id=' + supportId, {
					headers: { Authorization: 'Bearer ' + this.token },
				})
				.then((res) => {
					const data = res.data.data || {};
					this.support = data.support || {};
					this.work_service = data.work_service || null;
					this.logs = data.logs || [];
				})
				.catch((err) => {
					this.$swal('Error', this.$getApiErrorMessage?.(err, 'Failed to load support') || 'Failed to load support', 'error');
				});
		},
		fetchTechnicians() {
			this.getToken();
			this.$axios
				.get('/staffs/technicians', { headers: { Authorization: 'Bearer ' + this.token } })
				.then((res) => {
					const data = res.data.data || {};
					this.technicians = Array.isArray(data.technicians) ? data.technicians : (Array.isArray(data) ? data : []);
				})
				.catch(() => { this.technicians = []; });
		},
		openStatusModal() {
			this.status_type = '';
			this.technician_id = '';
			this.service_type = '';
			this.vehicle_number = '';
			this.remarks = this.support.details || '';
			this.modal_title = `Update Status - Support #${this.support.id}`;
			this.fetchTechnicians();
			this.showModal();
		},
		closeStatusModal() {
			this.hideModal();
		},
		onStatusTypeChange() {
			if (this.status_type != '5') {
				this.technician_id = '';
			}
		},
		submitStatusUpdate() {
			if (!this.status_type) {
				this.$swal('Error', 'Please select status', 'error');
				return;
			}
			if (!String(this.remarks || '').trim()) {
				this.$swal('Error', 'Remarks is required', 'error');
				return;
			}
			if (this.status_type == '5' && !this.technician_id) {
				this.$swal('Error', 'Please select technician', 'error');
				return;
			}

			this.submitting = true;
			this.getToken();

			const payload = {
				support_id: this.support.id,
				status: Number(this.status_type),
				remarks: this.remarks,
			};
			if (this.status_type == '5') {
				payload.technician_id = Number(this.technician_id);
			}
			if (this.status_type == '6') {
				if (this.service_type) payload.service_type = Number(this.service_type);
				if (this.vehicle_number) payload.vehicle_number = this.vehicle_number;
			}

			this.$axios
				.post('/staffs/updateSupportStatus', payload, {
					headers: { Authorization: 'Bearer ' + this.token },
				})
				.then((res) => {
					this.submitting = false;
					this.$swal('Success', res.data.message || 'Status updated successfully', 'success');
					this.closeStatusModal();
					this.fetchDetail();
				})
				.catch((err) => {
					this.submitting = false;
					this.$swal('Error', this.$getApiErrorMessage?.(err, 'Failed to update status') || 'Failed to update status', 'error');
				});
		},
	},
};
</script>

<style>
@import '@/assets/css/custom.css';
</style>
